import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx"; 
import JSZip from "jszip";


const API_URL = "https://service.galosolar.com/api/admin/getPhotosByDate";
const DETECT_OBJECTS = true; // COCO-SSD runs in the browser; false = no obstacle avoidance
const BASE_URL = "https://service.galosolar.com/api";
const FONT = "Arial, Helvetica, sans-serif";
const LABEL_BG_OPACITY = 0.55; 
const LABEL_TEXT_COLOR = "#000000";


const COLUMN_ALIASES = {
  latitude: ["latitude", "lat"],
  longitude: ["longitude", "long", "lng", "lon"],
  saralId: ["saralid", "saralno", "saral"],
  farmerName: ["farmername", "nameoffarmer", "farmer"],
  district: ["district"],
  village: ["village"],
  pumpCategory: ["categoryofpump", "pumpcategory", "category"],
  pumpCapacity: ["pumpcapacity", "capacity"],
  pumpSubType: ["pumpsubtype", "subtype"],
};


const norm = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const MAX_DAYS = 30;
const DAY_MS = 86400000;
const utc = (d) => Date.parse(`${d}T00:00:00Z`);
const daysInRange = (a, b) => Math.round((utc(b) - utc(a)) / DAY_MS) + 1; // both days counted
const checkRange = (start, end) => {
  if (!start || !end) return "Start date and end date are both required.";
  if (start > end) return "The start date must be on or before the end date.";
  const n = daysInRange(start, end);
  return n > MAX_DAYS ? `You can select a maximum of ${MAX_DAYS} days. This range is ${n} days.` : "";
};
const safeName = (s) => String(s).replace(/[\\/:*?"<>|]/g, "_");
const buildUrl = (p) => BASE_URL.replace(/\/+$/, "") + "/" + String(p).replace(/^\/+/, "");
const imageType = (p) => p.split("/").filter(Boolean)[1] || "image";
const prettyType = (t) => {
  const s = t.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// The API can repeat a saralId -> merge and de-duplicate images
const groupBySaralId = (data = []) => {
  const map = new Map();
  for (const { saralId, images = [] } of data) {
    if (!map.has(saralId)) map.set(saralId, new Set());
    images.forEach((i) => map.get(saralId).add(i));
  }
  return [...map].map(([saralId, set]) => ({ saralId, images: [...set] }));
};

// Excel header -> column map, built once per file
const mapColumns = (keys) => {
  const cols = {};
  keys.forEach((c) => {
    const n = norm(c);
    for (const k in COLUMN_ALIASES) if (!cols[k] && COLUMN_ALIASES[k].includes(n)) cols[k] = c;
  });
  return cols;
};

const toLabelLines = (row, c) => {
  const v = (k) => String(row[c[k]] ?? "").trim();
  const pump = [v("pumpCategory"), v("pumpCapacity"), v("pumpSubType")].filter(Boolean).join(" ");
  const districtAndVillage = [v("district"), v("village")].filter(Boolean).join(", ");
  return [
    ["Latitude", v("latitude")],
    ["Longitude", v("longitude")],
    ["Saral Id", v("saralId")],
    ["Farmer Name", v("farmerName")],
    ["District", districtAndVillage],
    ["Pump Capacity", pump],
  ].map(([k, val]) => `${k}: ${val}`);
};

const runPool = async (items, limit, worker) => {
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (i < items.length) await worker(items[i++]);
    })
  );
};

const saveBlob = (blob, filename) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1500);
};

/* ================================================================== */
/* In-browser detection (TensorFlow.js + COCO-SSD): people + panels    */
/* ================================================================== */
let modelPromise;
const loadModel = () =>
  (modelPromise ??= Promise.all([import("@tensorflow/tfjs"), import("@tensorflow-models/coco-ssd")])
    .then(([, coco]) => coco.load({ base: "mobilenet_v2" }))
    .catch((e) => ((modelPromise = null), Promise.reject(e))));

// COCO-SSD has no "solar panel" class, so panels are found by colour:
// dark, blue-leaning pixels grouped into large connected blocks.
const findPanels = (img) => {
  const cw = 160;
  const ch = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * cw));
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, cw, ch);
  const { data } = ctx.getImageData(0, 0, cw, ch);

  const S = 4; // block size in px
  const gw = Math.floor(cw / S), gh = Math.floor(ch / S);
  const on = new Uint8Array(gw * gh);
  for (let by = 0; by < gh; by++)
    for (let bx = 0; bx < gw; bx++) {
      let hits = 0;
      for (let y = 0; y < S; y++)
        for (let x = 0; x < S; x++) {
          const i = ((by * S + y) * cw + bx * S + x) * 4;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          if (b > r + 12 && b >= g - 4 && 0.299 * r + 0.587 * g + 0.114 * b < 140) hits++;
        }
      if (hits >= S * S * 0.5) on[by * gw + bx] = 1;
    }

  const seen = new Uint8Array(on.length);
  const out = [];
  for (let s0 = 0; s0 < on.length; s0++) {
    if (!on[s0] || seen[s0]) continue;
    const stack = [s0];
    seen[s0] = 1;
    let n = 0, x0 = gw, y0 = gh, x1 = 0, y1 = 0;
    while (stack.length) {
      const p = stack.pop();
      const x = p % gw, y = (p / gw) | 0;
      n++;
      x0 = Math.min(x0, x); x1 = Math.max(x1, x);
      y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      for (const q of [x > 0 && p - 1, x < gw - 1 && p + 1, y > 0 && p - gw, y < gh - 1 && p + gw]) {
        if (q !== false && on[q] && !seen[q]) {
          seen[q] = 1;
          stack.push(q);
        }
      }
    }
    if (n >= gw * gh * 0.015) out.push({ x: x0 / gw, y: y0 / gh, w: (x1 - x0 + 1) / gw, h: (y1 - y0 + 1) / gh });
  }
  return out; // fractions 0..1
};

const detectBoxes = async (img) => {
  const [model, el] = await Promise.all([loadModel(), loadImage(buildUrl(img))]);
  const W = el.naturalWidth, H = el.naturalHeight;
  const preds = await model.detect(el, 20, 0.35);
  const people = preds
    .filter((p) => p.class === "person")
    .map(({ bbox: [x, y, w, h] }) => ({ x: Math.max(0, x / W), y: Math.max(0, y / H), w: w / W, h: h / H }));
  return [...people, ...findPanels(el)];
};

const boxCache = new Map(); // image path -> Promise<boxes>
const getBoxes = (img) => {
  if (!boxCache.has(img)) {
    boxCache.set(img, detectBoxes(img).catch((e) => (boxCache.delete(img), Promise.reject(e))));
  }
  return boxCache.get(img);
};

/* ================================================================== */
/* Label layout: sizes the text + white container from the image size */
/* always bottom-left; shrinks until it clears every obstacle         */
/* ================================================================== */

let measureCtx;
const measure = (text, size) => {
  measureCtx ??= document.createElement("canvas").getContext("2d");
  measureCtx.font = `${size}px ${FONT}`;
  return measureCtx.measureText(text).width;
};

const overlap = (a, b) =>
  Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

export const computeLayout = (W, H, lines, boxes = []) => {
  const gapX = W * 0.02, gapY = H * 0.02; // breathing room around obstacles
  const obstacles = boxes.map((b) => ({
    x: b.x * W - gapX, y: b.y * H - gapY, w: b.w * W + 2 * gapX, h: b.h * H + 2 * gapY,
  }));
  const maxFs = Math.max(14, W * 0.02);
  const minFs = Math.max(10, W * 0.009);
  let best = null;
  let fs = maxFs;

  for (;;) {
    const pad = Math.round(fs * 0.6);
    const lineH = Math.round(fs * 1.35);
    const w = Math.ceil(Math.max(...lines.map((l) => measure(l, fs)))) + pad * 2;
    const h = lines.length * lineH + pad * 2;
    const make = (x, y, ov) => ({ x, y, w, h, fontSize: fs, pad, lineH, W, H, ov });

    if (w <= W * 0.55 && h <= H * 0.4) {
      // position is fixed: bottom-left corner, no margin. Only the size changes.
      const ov = obstacles.reduce((sum, o) => sum + overlap({ x: 0, y: H - h, w, h }, o), 0);
      if (ov === 0) return make(0, H - h, 0);
      best = make(0, H - h, ov);
    }
    if (fs <= minFs) break;
    fs = Math.max(minFs, fs * 0.92); // nothing free at this size -> shrink and retry
  }
  const pad = 6, lineH = Math.round(minFs * 1.35);
  return best ?? { x: 0, y: H - lines.length * lineH - pad * 2, w: 0, h: 0, fontSize: minFs, pad, lineH, W, H, ov: 0 };
};

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // image server must send CORS headers
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load " + url));
    img.src = url;
  });

// Draws photo + semi-transparent white label, returns a JPEG blob
const renderLabeled = async (url, lines, boxes) => {
  const img = await loadImage(url);
  const canvas = document.createElement("canvas");
  const W = (canvas.width = img.naturalWidth);
  const H = (canvas.height = img.naturalHeight);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);

  if (lines) {
    const L = computeLayout(W, H, lines, boxes);
    ctx.fillStyle = `rgba(255,255,255,${LABEL_BG_OPACITY})`; // semi-transparent white container
    ctx.fillRect(L.x, L.y, L.w, L.h);
    ctx.fillStyle = LABEL_TEXT_COLOR; // text is drawn after, so it stays fully opaque
    ctx.font = `${L.fontSize}px ${FONT}`;
    ctx.textBaseline = "middle";
    lines.forEach((l, i) => ctx.fillText(l, L.x + L.pad, L.y + L.pad + L.lineH * (i + 0.5)));
  }

  const blob = await new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("Export failed"))), "image/jpeg", 0.92)
  );
  canvas.width = canvas.height = 0; // free memory
  return blob;
};

/* ================================================================== */
/* UI pieces                                                           */
/* ================================================================== */
const ICONS = {
  upload: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M17 8l-5-5-5 5", "M12 3v12"],
  download: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M7 10l5 5 5-5", "M12 15V3"],
  search: ["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z", "M21 21l-4.3-4.3"],
  x: ["M18 6L6 18", "M6 6l12 12"],
  check: ["M20 6L9 17l-5-5"],
  expand: ["M15 3h6v6", "M9 21H3v-6", "M21 3l-7 7", "M3 21l7-7"],
  sheet: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M8 13h8", "M8 17h8"],
  image: ["M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M21 15l-5-5L5 21", "M8.5 8h.01"],
};

const Icon = ({ name, className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    {ICONS[name].map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

const Spinner = ({ className = "h-4 w-4" }) => (
  <span className={`${className} inline-block animate-spin rounded-full border-2 border-current border-t-transparent`} />
);

const Checkbox = ({ checked, onChange, label, onImage = false, disabled = false }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange();
    }}
    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${
      checked
        ? "border-yellow-600 bg-yellow-600 text-black"
        : onImage
        ? "border-white bg-black/40 text-transparent enabled:hover:border-yellow-600"
        : "border-zinc-400 bg-white text-transparent enabled:hover:border-yellow-600"
    }`}
  >
    <Icon name="check" className="h-3.5 w-3.5" />
  </button>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-zinc-50 px-3 py-2.5 ring-1 ring-zinc-200">
    <div className="text-xl font-bold tabular-nums">{value}</div>
    <div className="text-xs text-zinc-500">{label}</div>
  </div>
);

const Step = ({ n, title, hint, children }) => (
  <section className="px-4 py-5 sm:px-5">
    <div className="mb-3 flex items-center gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black text-xs font-bold text-yellow-600">{n}</span>
      <div>
        <h2 className="text-sm font-semibold leading-tight">{title}</h2>
        <p className="text-xs text-zinc-500">{hint}</p>
      </div>
    </div>
    {children}
  </section>
);

// One photo. Memoised: only re-renders when its own props change.
const Tile = memo(function Tile({ img, saralId, order, assignedId, meta, onToggle, onPreview, onLoaded }) {
  const checked = order !== undefined; // order = position in the selection (1, 2, 3 ...)
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const ref = useRef(null);
  const loaded = status === "loaded" && !!meta;

  const handleLoad = useCallback(
    (el) => {
      setStatus("loaded");
      onLoaded(img, el.naturalWidth, el.naturalHeight);
    },
    [img, onLoaded]
  );

  useEffect(() => {
    const el = ref.current; // image may already be cached
    if (el?.complete && el.naturalWidth) handleLoad(el);
  }, [attempt, handleLoad]);

  const ratio = meta ? meta.w / meta.h : 4 / 3;
  const type = prettyType(imageType(img));

  return (
    <figure className="group min-w-0">
      <div
        onClick={() => loaded && onToggle(img)}
        className={`relative aspect-[4/3] overflow-hidden rounded-lg bg-zinc-900 transition ${
          checked ? "ring-2 ring-yellow-600 ring-offset-2" : "ring-1 ring-zinc-200 hover:ring-zinc-400"
        } ${loaded ? "cursor-pointer" : "cursor-not-allowed"}`}
      >
        {status === "loading" && <div className="absolute inset-0 animate-pulse bg-zinc-300" />}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-100 p-2 text-center text-xs text-zinc-600">
            Could not load
            <button
              onClick={(e) => {
                e.stopPropagation();
                setStatus("loading");
                setAttempt((a) => a + 1);
              }}
              className="rounded bg-black px-2.5 py-1 font-semibold text-yellow-600"
            >
              Retry
            </button>
          </div>
        )}

        <div className={`absolute inset-0 flex items-center justify-center ${status === "error" ? "hidden" : ""}`}>
          <div
            className="relative"
            style={{ aspectRatio: ratio, width: ratio >= 4 / 3 ? "100%" : `${(ratio / (4 / 3)) * 100}%` }}
          >
            <img
              key={attempt}
              ref={ref}
              src={buildUrl(img) + (attempt ? `?retry=${attempt}` : "")}
              alt={`${saralId} ${type}`}
              crossOrigin="anonymous"
              loading="lazy"
              decoding="async"
              draggable={false}
              onLoad={(e) => handleLoad(e.currentTarget)}
              onError={() => setStatus("error")}
              className="block h-full w-full"
            />
          </div>
        </div>

        <div className="absolute left-2 top-2">
          <Checkbox onImage checked={checked} disabled={!loaded} onChange={() => onToggle(img)} label={`Select ${type} for ${saralId}`} />
        </div>
        {loaded && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(img, saralId);
            }}
            aria-label="Preview photo"
            className="absolute right-2 top-2 rounded-md bg-black/60 p-1.5 text-white transition hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
          >
            <Icon name="expand" className="h-3.5 w-3.5" />
          </button>
        )}
        {checked && (
          <span className="pointer-events-none absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate rounded bg-black px-2 py-1 text-[10px] font-semibold text-yellow-600">
            #{order} · {assignedId || "no Excel row"}
          </span>
        )}
      </div>
      <figcaption className="mt-1.5 truncate text-xs text-zinc-600">{type}</figcaption>
    </figure>
  );
});

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */
export default function PhotosByDate() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const [selected, setSelected] = useState([]); // ordered list of image paths
  const [meta, setMeta] = useState({}); // img -> {w,h}; only set once the image has loaded
  const [excel, setExcel] = useState({ name: "", rows: [] }); // rows: [{ id, lines }] in sheet order
  const [dragging, setDragging] = useState(false);

  const [busy, setBusy] = useState(null); // {phase, done, total}
  const [failed, setFailed] = useState([]);
  const [preview, setPreview] = useState(null);

  const fileRef = useRef(null);
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const rowsCountRef = useRef(0);
  rowsCountRef.current = excel.rows.length;

  /* ---- derived data ---- */
  const ownerOf = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => g.images.forEach((i) => m.set(i, g.saralId)));
    return m;
  }, [groups]);
  const ownerRef = useRef(ownerOf);
  ownerRef.current = ownerOf;

  // image path -> position in the selection (0-based)
  const orderOf = useMemo(() => new Map(selected.map((img, i) => [img, i])), [selected]);

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? groups.filter((g) => g.saralId.toLowerCase().includes(q)) : groups;
  }, [groups, query]);

  const totalImages = useMemo(() => groups.reduce((n, g) => n + g.images.length, 0), [groups]);
  const visibleLoaded = useMemo(
    () => visibleGroups.flatMap((g) => g.images).filter((i) => meta[i]),
    [visibleGroups, meta]
  );

  /* ---- fetch ---- */
  const fetchPhotos = async () => {
    setError("");
    if (!excel.name) return setError("Upload the Excel sheet first.");
    const rangeMsg = checkRange(startDate, endDate);
    if (rangeMsg) return setError(rangeMsg);
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startDate, endDate }),
      });
      if (!res.ok) throw new Error(`The server responded with status ${res.status}.`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "The request was not successful.");
      setGroups(groupBySaralId(json.data));
      setSelected([]);
      setMeta({});
      setFailed([]);
      setQuery("");
    } catch (e) {
      setError(e.message || "Something went wrong. Try again.");
      setGroups([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  /* ---- selection (loaded images only) ---- */
  const onLoaded = useCallback(
    (img, w, h) => setMeta((p) => (p[img]?.w === w && p[img]?.h === h ? p : { ...p, [img]: { w, h } })),
    []
  );

  // One photo per fetched Saral ID. Picking another photo of the same ID replaces
  // the earlier pick in the same slot, so its Excel row stays the same.
  const toggleImage = useCallback((img) => {
    if (!metaRef.current[img]) return;
    const prev = selectedRef.current;
    let next;
    if (prev.includes(img)) {
      next = prev.filter((x) => x !== img);
    } else {
      const owner = ownerRef.current.get(img);
      const j = prev.findIndex((x) => ownerRef.current.get(x) === owner);
      if (j >= 0) {
        next = [...prev];
        next[j] = img;
      } else if (prev.length >= rowsCountRef.current) {
        const n = rowsCountRef.current;
        setError(`The Excel sheet has ${n} row${n === 1 ? "" : "s"}, so you can select at most ${n} photo${n === 1 ? "" : "s"}.`);
        return;
      } else {
        next = [...prev, img];
      }
    }
    setError("");
    setSelected(next);
  }, []);

  const onPreview = useCallback((img, saralId) => setPreview({ img, saralId }), []);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  /* ---- excel ---- */
  const processFile = async (file) => {
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const cols = mapColumns(Object.keys(rows[0] || {}));
      if (!cols.saralId) throw new Error("No Saral ID column found in the sheet.");
      const parsed = rows
        .map((r) => ({ id: String(r[cols.saralId] ?? "").trim(), lines: toLabelLines(r, cols) }))
        .filter((r) => r.id);
      if (!parsed.length) throw new Error("The sheet has no rows with a Saral ID.");
      setExcel({ name: file.name, rows: parsed });
      setError("");
    } catch (e) {
      setError(e.message || "That file could not be read. Upload an .xlsx, .xls or .csv file.");
    }
  };

  const clearExcel = () => {
    setExcel({ name: "", rows: [] });
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ---- ZIP download ---- */
  const downloadZip = async () => {
    if (!selected.length) return;
    if (selected.length > excel.rows.length) {
      return setError(`Select at most ${excel.rows.length} photos. The Excel sheet only has that many rows.`);
    }
    // i-th selected photo gets the i-th Excel row; file name is that row's Saral ID
    const used = {};
    const jobs = selected.map((img, i) => {
      const { id, lines } = excel.rows[i];
      const n = used[id] || 0;
      used[id] = n + 1;
      const base = safeName(id);
      return { img, lines, name: n ? `${base}(${n}).jpg` : `${base}.jpg` };
    });

    setFailed([]);
    setBusy({ phase: "Labeling photos", done: 0, total: jobs.length });
    const zip = new JSZip();
    const bad = [];

    await runPool(jobs, 3, async (j) => {
      try {
        const boxes = j.lines && DETECT_OBJECTS ? await getBoxes(j.img).catch(() => []) : [];
        zip.file(j.name, await renderLabeled(buildUrl(j.img), j.lines, boxes));
      } catch {
        bad.push(j.name);
      }
      setBusy((b) => ({ ...b, done: b.done + 1 }));
    });

    if (bad.length < jobs.length) {
      setBusy({ phase: "Creating ZIP", done: 0, total: 100 });
      const blob = await zip.generateAsync({ type: "blob", compression: "STORE" }, (m) =>
        setBusy((b) => ({ ...b, done: Math.round(m.percent) }))
      );
      saveBlob(blob, `photos_${startDate}_${endDate}.zip`);
    }
    setFailed(bad);
    setBusy(null);
  };

  /* ---- preview modal data ---- */
  const pMeta = preview && meta[preview.img];

  const rangeError = startDate && endDate ? checkRange(startDate, endDate) : "";
  const dateInput = "mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2.5 py-2 text-sm text-black focus:border-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-600/40";

  /* ================================================================ */
  return (
    <div className="min-h-screen bg-zinc-100 pb-32 text-black antialiased">

      <div className="mx-auto grid max-w-[1400px] gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-6 lg:grid-cols-[330px_minmax(0,1fr)] lg:items-start">
        {/* ---------------- Sidebar ---------------- */}
        <aside className="grid divide-y divide-zinc-200 rounded-xl bg-white shadow-sm ring-1 ring-zinc-200 md:grid-cols-3 md:divide-x md:divide-y-0 lg:sticky lg:top-6 lg:grid-cols-1 lg:divide-x-0 lg:divide-y">
          <Step n="1" title="Upload farmer details" hint="Required. Rows are used in selection order">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => processFile(e.target.files?.[0])} className="hidden" />
            {!excel.name ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => (e.preventDefault(), setDragging(true))}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => (e.preventDefault(), setDragging(false), processFile(e.dataTransfer.files?.[0]))}
                className={`flex w-full flex-col items-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600 ${
                  dragging ? "border-yellow-600 bg-yellow-600/10" : "border-zinc-300 hover:border-yellow-600 hover:bg-zinc-50"
                }`}
              >
                <Icon name="upload" className="h-5 w-5 text-zinc-500" />
                <span className="text-sm font-medium">Drop an Excel file or browse</span>
                <span className="text-xs text-zinc-500">.xlsx, .xls or .csv</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 rounded-lg bg-zinc-50 p-3 ring-1 ring-zinc-200">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-black text-yellow-600">
                  <Icon name="sheet" className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{excel.name}</div>
                  <div className="text-xs text-zinc-500">{excel.rows.length} rows found</div>
                </div>
                <button onClick={clearExcel} aria-label="Remove Excel file" className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-200 hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600">
                  <Icon name="x" />
                </button>
              </div>
            )}
          </Step>

          <Step n="2" title="Choose a date range" hint="Both dates required · maximum 30 days">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-medium text-zinc-600">
                Start date <span aria-hidden="true">*</span>
                <input type="date" required value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} className={dateInput} />
              </label>
              <label className="block text-xs font-medium text-zinc-600">
                End date <span aria-hidden="true">*</span>
                <input type="date" required value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} className={dateInput} />
              </label>
            </div>
            {rangeError && (
              <p role="alert" className="mt-3 border-l-4 border-black bg-yellow-600/15 px-3 py-2 text-xs font-medium">
                {rangeError}
              </p>
            )}
            <button
              onClick={fetchPhotos}
              disabled={loading || !excel.name || !!rangeError}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-600 px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-yellow-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <Spinner />}
              {loading ? "Loading photos" : "Get photos"}
            </button>
            {!excel.name && <p className="mt-2 text-xs text-zinc-500">Upload the Excel sheet first to enable this.</p>}
          </Step>

          <Step n="3" title="Select and download" hint="Labels are added when you download">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Saral IDs" value={groups.length} />
              <Stat label="Photos" value={totalImages} />
              <Stat label="Selected" value={selected.length} />
              <Stat label="Excel rows" value={excel.name ? excel.rows.length : "–"} />
            </div>
          </Step>
        </aside>

        {/* ---------------- Results ---------------- */}
        <main className="min-w-0">
          {error && (
            <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-lg border-l-4 border-black bg-yellow-600/15 px-4 py-3 text-sm">
              <span>{error}</span>
              <button onClick={() => setError("")} aria-label="Dismiss" className="shrink-0 text-zinc-600 hover:text-black">
                <Icon name="x" />
              </button>
            </div>
          )}
          {failed.length > 0 && (
            <div role="alert" className="mb-4 rounded-lg border-l-4 border-black bg-white px-4 py-3 text-sm shadow-sm ring-1 ring-zinc-200">
              {failed.length} photo(s) were skipped. Check that the image server allows CORS.
              <span className="mt-1 block break-all text-xs text-zinc-500">{failed.join(", ")}</span>
            </div>
          )}

          {groups.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-zinc-200">
              <span className="text-sm font-medium">Select one photo per Saral ID</span>
              <span className="text-sm text-zinc-500">
                {visibleLoaded.length} loaded · {visibleGroups.length} Saral IDs
              </span>
              <div className="relative w-full sm:ml-auto sm:w-64">
                <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search Saral ID"
                  className="w-full rounded-lg border border-zinc-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-600/40"
                />
              </div>
            </div>
          )}

          {loading && (
            <div className="space-y-4">
              {[0, 1].map((k) => (
                <div key={k} className="animate-pulse rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
                  <div className="mb-4 h-5 w-48 rounded bg-zinc-200" />
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="aspect-[4/3] rounded-lg bg-zinc-200" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && groups.length === 0 && (
            <div className="flex flex-col items-center rounded-xl bg-white px-6 py-16 text-center shadow-sm ring-1 ring-zinc-200 sm:py-20">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-black text-yellow-600">
                <Icon name="image" className="h-6 w-6" />
              </span>
              <h2 className="text-base font-semibold">{fetched && !error ? "No photos in this date range" : "No photos loaded yet"}</h2>
              <p className="mt-1 max-w-sm text-sm text-zinc-500">
                {fetched && !error ? "Try a wider date range." : "Upload the Excel sheet, choose a start and end date, then select Get photos."}
              </p>
            </div>
          )}

          {!loading && groups.length > 0 && visibleGroups.length === 0 && (
            <div className="rounded-xl bg-white px-6 py-12 text-center text-sm text-zinc-500 shadow-sm ring-1 ring-zinc-200">No Saral ID matches “{query}”.</div>
          )}

          {!loading && (
            <div className="space-y-4 sm:space-y-5">
              {visibleGroups.map((g) => {
                const picked = g.images.some((i) => orderOf.has(i));
                return (
                  <section key={g.saralId} className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
                    <header className="flex flex-wrap items-center gap-2 border-b border-zinc-200 px-4 py-3 sm:gap-3">
                      <h2 className="text-base font-semibold tabular-nums">{g.saralId}</h2>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          picked ? "bg-yellow-600 text-black" : "bg-zinc-100 text-zinc-600"
                        }`}
                      >
                        {picked ? "1 selected" : "Choose 1 photo"} · {g.images.length} available
                      </span>
                    </header>
                    <div className="grid grid-cols-2 gap-2.5 p-3 sm:gap-3 sm:p-4 md:grid-cols-3 xl:grid-cols-4">
                      {g.images.map((img) => (
                        <Tile
                          key={img}
                          img={img}
                          saralId={g.saralId}
                          order={orderOf.has(img) ? orderOf.get(img) + 1 : undefined}
                          assignedId={excel.rows[orderOf.get(img)]?.id}
                          meta={meta[img]}
                          onToggle={toggleImage}
                          onPreview={onPreview}
                          onLoaded={onLoaded}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Floating action bar */}
      {selected.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-3 z-30 flex justify-center px-3 sm:bottom-5 sm:px-4">
          <div className="pointer-events-auto flex w-full max-w-xl flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-black px-4 py-3 text-white shadow-2xl ring-1 ring-white/10">
            <div className="min-w-[10rem] flex-1">
              <div className="text-sm font-semibold">
                {busy ? `${busy.phase} · ${busy.done}/${busy.total}` : `${selected.length} photo${selected.length > 1 ? "s" : ""} selected`}
              </div>
              {busy ? (
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full rounded-full bg-yellow-600 transition-all" style={{ width: `${(busy.done / busy.total) * 100}%` }} />
                </div>
              ) : (
                <div className="text-xs text-zinc-400">
                  Excel rows are applied in the order you selected
                </div>
              )}
            </div>
            {!busy && (
              <button onClick={() => setSelected([])} className="rounded-lg px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600">
                Clear
              </button>
            )}
            <button
              onClick={downloadZip}
              disabled={!!busy}
              className="flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 text-sm font-semibold text-black transition hover:bg-yellow-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
            >
              {busy ? <Spinner /> : <Icon name="download" />}
              Download ZIP
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && pMeta && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/85 p-3 sm:p-4" onClick={() => setPreview(null)} role="dialog" aria-modal="true">
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-4 text-white">
              <span className="truncate text-sm font-medium">
                {preview.saralId} · {prettyType(imageType(preview.img))}
              </span>
              <button onClick={() => setPreview(null)} aria-label="Close preview" className="rounded-md p-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600">
                <Icon name="x" className="h-5 w-5" />
              </button>
            </div>
            <div
              className="relative mx-auto overflow-hidden rounded-lg bg-black"
              style={{ aspectRatio: pMeta.w / pMeta.h, width: `min(100%, calc(80vh * ${pMeta.w / pMeta.h}))` }}
            >
              <img src={buildUrl(preview.img)} alt="" crossOrigin="anonymous" className="block h-full w-full" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


